import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { Trophy, MapPin } from 'lucide-react';
import { avatarSrc } from '../avatar/avatarList';
import './MultiplayerResults.css';

const Avatar = ({ avatar, name, className = '' }) => (
  <div className={`mp-avatar ${className}`}>
    {avatar
      ? <img src={avatarSrc(avatar)} alt="" />
      : (name?.charAt(0).toUpperCase() || '?')}
  </div>
);

const PodiumSlot = ({ player, rank, isCurrentUser }) => {
  if (!player) return <div className={`mp-podium-slot mp-podium-slot--${rank} mp-podium-slot--empty`} />;

  return (
    <div className={`mp-podium-slot mp-podium-slot--${rank}${isCurrentUser ? ' mp-podium-slot--you' : ''}`}>
      <div className="mp-podium-above">
        {rank === 1 && (
          <div className="mp-podium-crown"><Trophy size={18} /></div>
        )}
        <div className={`mp-podium-avatar mp-podium-avatar--${rank}`}>
          {player.avatar
            ? <img src={avatarSrc(player.avatar)} alt="" />
            : player.user_name?.charAt(0).toUpperCase()}
        </div>
        <div className="mp-podium-info">
          <span className="mp-podium-name">
            {player.user_name?.split(' ')[0]}
            {isCurrentUser && <span className="you-tag">You</span>}
          </span>
          <span className="mp-podium-score">{player.total_score?.toLocaleString()} pts</span>
        </div>
      </div>
      <div className={`mp-podium-platform mp-podium-platform--${rank}`}>
        <span className="mp-podium-rank-num">{rank}</span>
      </div>
    </div>
  );
};

const MultiplayerResults = ({ results, onPlayAgain }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const standings = results.standings || [];
  const top3 = standings.slice(0, 3);
  const rest = standings.slice(3);

  return (
    <div className="multiplayer-results-overlay">
      <div className="multiplayer-results-card">
        {/* Header */}
        <div className="mp-results-header">
          <h1>Final Results</h1>
          <p>Game Complete</p>
        </div>

        <div className="results-content">
          {/* Podium — top 3 */}
          {top3.length > 0 && (
            <div className="mp-podium">
              <PodiumSlot player={top3[1]} rank={2} isCurrentUser={top3[1]?.user_id === user?.id} />
              <PodiumSlot player={top3[0]} rank={1} isCurrentUser={top3[0]?.user_id === user?.id} />
              <PodiumSlot player={top3[2]} rank={3} isCurrentUser={top3[2]?.user_id === user?.id} />
            </div>
          )}

          {/* Final Standings — rank 4+ */}
          {rest.length > 0 && (
            <div className="standings-section">
              <h3 className="section-title">Final Standings</h3>
              <div className="standings-table">
                {rest.map((player, index) => {
                  const isYou = player.user_id === user?.id;
                  return (
                    <div
                      key={player.user_id}
                      className={`mp-row ${isYou ? 'mp-row-you' : ''}`}
                    >
                      <span className="mp-row-rank">#{index + 4}</span>
                      <Avatar avatar={player.avatar} name={player.user_name} />
                      <div className="mp-row-info">
                        <span className="mp-row-name">
                          {player.user_name}
                          {isYou && <span className="you-tag">You</span>}
                        </span>
                      </div>
                      <span className="mp-row-score">{player.total_score?.toLocaleString()}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Round Breakdown */}
          {results.rounds && results.rounds.length > 0 && (
            <div className="rounds-section">
              <h3 className="section-title">Round Breakdown</h3>
              <div className="rounds-list">
                {results.rounds.map((round) => (
                  <div key={round.round_number} className="round-item">
                    <div className="round-item-header">
                      <span className="round-number">Round {round.round_number}</span>
                      <span className="round-location">
                        <MapPin size={14} />
                        {round.location?.name}
                      </span>
                    </div>
                    {round.location?.description && (
                      <p className="round-description">{round.location.description}</p>
                    )}
                    {round.location?.facts?.length > 0 && (
                      <ul className="round-facts">
                        {round.location.facts.map((fact, i) => (
                          <li key={i}>{fact}</li>
                        ))}
                      </ul>
                    )}
                    <div className="round-top-scores">
                      {round.guesses?.slice(0, 3).map((guess, idx) => (
                        <div key={guess.user_id} className="top-score-item">
                          <span className="top-rank">{idx + 1}</span>
                          <Avatar avatar={guess.avatar} name={guess.user_name} className="mp-avatar--sm" />
                          <span className="top-name">{guess.user_name}</span>
                          <span className="top-points">
                            {guess.is_timeout ? 'Timeout' : `${guess.score?.toLocaleString()} pts`}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="results-actions">
          <button className="action-btn primary" onClick={onPlayAgain}>
            Play Again
          </button>
          <button className="action-btn secondary" onClick={() => navigate('/multiplayer')}>
            Back to Multiplayer
          </button>
        </div>
      </div>
    </div>
  );
};

export default MultiplayerResults;
